import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import heartLoading from "../../../assests/heartLoading.gif";
import { Link, useSearchParams } from "react-router-dom";
import { MetricCard } from "../ui/MetricCard";
import { FaArrowLeft, FaExternalLinkAlt } from "react-icons/fa";
import { Database, Eye, LineChart, ListFilter } from "lucide-react";
import "./SubdomainDetail.scss";
import Tooltip from "@mui/material/Tooltip";
import axiosInstance from "../../../axiosInstanceSetup";
import LogoImage from "../../ProductLogo/LogoImage";
import { ShoppingCart, ShoppingBag } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  ADD_TO_CART,
  REMOVE_FROM_CART,
} from "../../../../src/redux/domainAddToCart/domainAddToCart.type";
import ERLandingPage from "../ERDiagram/pages/ERLandingPage";
import ProductModal from "../DataContract/ProductModal";
import { CiCircleInfo } from "react-icons/ci";
import { useFeatureFlag } from "../../../context/FeatureFlagContext";
import { buildDataDomainUrl } from "../../../utils/dataDomainUrl";
import { AssetsDetailsUI } from "./AssetsDetailsUI";
import ReportDetails from "./ReportDetails";
import { FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import ReusableFilterDrawer from "../../common/ReusableFilterDrawer";
import AppliedFilters from "../../common/AppliedFilters";

const serviceNowTicketLink =
  "https://marsprod.service-now.com/sp?id=incident_helper";

export const AssetsSummary = ({ subdomain, onBack, onL5Click, onReportClick, productId, userRole }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(20);
  const tableContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const hasMoreRef = useRef(false);
  const totalFilteredRef = useRef(0);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productData, setProductData] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [selectedProductForView, setSelectedProductForView] = useState(null)
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.domainAddToCart);
  const isAddToCartEnabled = useFeatureFlag("addToCart");
  const [isReportView, setIsReportView] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetailData, setReportDetailData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(subdomain.product_description);
  const canEditDescription = ['Admin', 'Product Owner', 'Data Steward'].includes(userRole);
  const [displayDescription, setDisplayDescription] = useState(subdomain.product_description);
  const [isSaving, setIsSaving] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [assetFilters, setAssetFilters] = useState({ schema: [] });



  const [searchParams] = useSearchParams();

  const currentL1 = searchParams.get("l1");

  const currentL2 = searchParams.get("l2");

  const currentL3 = searchParams.get("l3");

  const currentL4 = searchParams.get("l4");

  useEffect(() => {

    if (!selectedReport) return;

    const fetchReportDetails = async () => {

      try {
        const params = new URLSearchParams();
        params.append("l1", currentL1);
        params.append("l2", currentL2);
        params.append("l3", currentL3);
        params.append("l4", currentL4);
        params.append("l5", selectedReport.report_name);
        params.append("type", "kpi");
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/domain-entity?${params.toString()}`
        );
        if (response.status === 200) {
          setReportDetailData(response.data);
        }
      } catch (error) {
        console.error("Error fetching report details:", error);
      }
    };
    fetchReportDetails();
  }, [selectedReport, currentL1, currentL2, currentL3, currentL4]);


  const mapAssetToCartItem = (asset) => ({
    id: asset.data_asset_id,
    name: asset.data_asset_name,
    productName: subdomain.product_name,
  });

  const isSelected = (assetId) => cartItems.some((item) => item.id === assetId);


  const filteredRows = useMemo(() => {
    return (subdomain?.Assets || []).filter((asset) => {
      const matchesSearch = asset.data_asset_name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesSchema =
        assetFilters.schema.length === 0 ||
        assetFilters.schema.includes(getPartBeforePipe(asset.data_asset_name));
      return matchesSearch && matchesSchema;
    });
  }, [subdomain?.Assets, searchQuery, assetFilters]);

  const currentRows = filteredRows.slice(0, visibleCount);
  const hasMore = visibleCount < filteredRows.length;

  // Reset on search change
  useEffect(() => {
    setVisibleCount(20);
    totalFilteredRef.current = filteredRows.length;
  }, [searchQuery, subdomain]);

  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);

  const handleIntersect = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMoreRef.current) {
      setVisibleCount((prev) => Math.min(prev + 20, totalFilteredRef.current));
    }
  }, []);
  const handleReportClick = (report) => {
    setSelectedReport(report);
    onL5Click(report.report_name, "kpi");
  };

  useEffect(() => {
    const el = sentinelRef.current;
    const container = tableContainerRef.current;
    if (!el || !container) return;
    const observer = new IntersectionObserver(handleIntersect, {
      root: container,
      rootMargin: "100px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const handleSelectAll = () => {
    const existingIds = new Set(cartItems.map((item) => item.id));

    const newItems = currentRows
      .filter((asset) => !existingIds.has(asset.data_asset_id))
      .map(mapAssetToCartItem);

    if (newItems.length) {
      dispatch({
        type: ADD_TO_CART,
        payload: newItems,
      });
    }
  };
  const handleSaveDescription = async () => {
    setIsSaving(true);
    try {
      await axiosInstance.put(`${process.env.REACT_APP_API_URL}/description`, {
        type: "product",
        items: [{ id: subdomain.product_id, new_description: editedDescription }],
      });
      setDisplayDescription(editedDescription);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating description:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveAll = () => {
    const pageIds = currentRows.map((asset) => asset.data_asset_id);

    dispatch({
      type: REMOVE_FROM_CART,
      payload: pageIds,
    });
  };

  const handleSelectSingle = (asset) => {
    if (isSelected(asset.data_asset_id)) {
      dispatch({
        type: REMOVE_FROM_CART,
        payload: [asset.data_asset_id],
      });
    } else {
      dispatch({
        type: ADD_TO_CART,
        payload: [mapAssetToCartItem(asset)],
      });
    }
  };
  const schemaOptions = useMemo(() => {
    const schemas = (subdomain?.Assets || [])
      .map((a) => getPartBeforePipe(a.data_asset_name))
      .filter(Boolean);
    return [...new Set(schemas)].map((s) => ({ value: s, label: s }));
  }, [subdomain?.Assets]);

  const filterSections = useMemo(() => [
    {
      fields: [
        {
          type: "multiselect",
          key: "schema",
          label: "Schema",
          options: schemaOptions,
        },
      ],
    },
  ], [schemaOptions]);

  const appliedChips = [
    ...(assetFilters.schema || []).map((s) => ({ label: "Schema", value: s })),
  ];

  const activeFilterCount = assetFilters.schema.length;

  function getPartAfterPipe(str) {
    if (!str) return "";
    const parts = str.split("|");
    return parts.length > 1 ? parts[1] : str;
  }
  function getPartBeforePipe(str) {
    if (!str) return "";
    const parts = str.split("|");
    return parts.length > 1 ? parts[0] : str;
  }
  const handleViewProduct = async () => {
    if (!subdomain?.product_name) return;
    setProductLoading(true);
    setShowProductModal(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/get-product-submission/${subdomain.product_name}`
      );
      if (response.status === 200) {
        setSelectedProductForView(response.data);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    } finally {
      setProductLoading(false);
    }
  };


  return (
    <div className="space-y-8">
      {/* Subdomain Summary */}
      <div className="rounded-lg p-4 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-1">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-black mb-4">
                {subdomain.product_name}
              </h1>
              <div>
                <div className="ml-1 inline-flex items-center gap-2 mb-4">
                  {subdomain.logoIds?.map((l) => (
                    <LogoImage key={l} logoId={l} />
                  ))}
                </div>
              </div>
            </div>


          </div>

          <div className="flex flex-col md:flex-row items-center space-x-3 gap-3 mt-2 md:mt-0 flex-shrink-0">


            <button
              onClick={onBack}
              className="h-[36px] text-[#bb1ccc] hover:text-white transition-colors duration-200 border border-[#bb1ccc] px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#bb1ccc] hover:shadow-lg whitespace-nowrap"
            >
              <FaArrowLeft /> Go Back
            </button>
          </div>


        </div>
        {isEditing ? (
          <>
            <textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              className="w-full p-3 border border-[#357EDB] rounded-lg text-lg text-black leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#357EDB] mb-3"
              rows="2"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveDescription}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-[#357EDB] text-white rounded-lg hover:bg-[#357EDB] transition-colors text-sm disabled:opacity-70"
              >
                {isSaving ? (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                ) : (
                  <FiCheck size={16} />
                )}
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => {
                  setEditedDescription(displayDescription);
                  setIsEditing(false);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-[#357EDB] text-[#357EDB] rounded-lg transition-colors text-sm"
              >
                <FiX size={16} /> Cancel
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-1">
            <p className="text-lg text-black leading-relaxed">
              {displayDescription}
            </p>
            {canEditDescription && (
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 text-[#14B8A6] flex-shrink-0"
                title="Edit"
              >
                <FiEdit2 size={14} />
              </button>
            )}
          </div>
        )}
        <div className="flex gap-2 mt-2 mb-3">
          <ERLandingPage />
          <a
            href={serviceNowTicketLink}
            target="_blank"
            rel="noopener noreferrer"
            className="h-[40px]   px-4 py-2 rounded-lg  border border-[#D8C7E6] flex items-center gap-2 text-[#BE123C]  whitespace-nowrap"
          >
            <CiCircleInfo size={20} className="text-[#BE123C]  text-lg" /> Raise an Incident
          </a>
          <button
            onClick={handleViewProduct}
            className="h-[40px] text-[#bb1ccc]    border border-[#D8C7E6] px-4 py-2 rounded-lg flex items-center gap-2   whitespace-nowrap"
          >
            <Eye className="h-5" />
            View Product
          </button>
        </div>
        {/* Summary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            title="Critical Data Elements"
            value={
              subdomain?.product_cde_count ? subdomain?.product_cde_count : "0"
            }
            variant="success"
          />
          <MetricCard
            title="Quality Score"
            value={subdomain?.product_score ? subdomain?.product_score : "0%"}
            variant="warning"
          />
          <MetricCard
            title={isReportView ? "Total Reports" : "Total Assets"}
            value={isReportView ? (subdomain?.Reports?.length ? subdomain?.Reports?.length : "0") : (subdomain?.total_assets ? subdomain?.total_assets : "0")}
            variant="primary"
          />
        </div>
      </div>
      {subdomain?.product_layer === "analyticalProduct" && subdomain?.Reports && subdomain.Reports.length > 0 && (
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium whitespace-nowrap ${!isReportView ? "text-[#4F46E5]" : "text-gray-400"}`}>
            Assets Details
          </span>
          <button
            onClick={() => setIsReportView(!isReportView)}
            className="relative inline-flex h-[26px] w-[50px] items-center rounded-full bg-[#4F46E5] transition-colors duration-300 focus:outline-none flex-shrink-0"
          >
            <span
              className={`inline-block h-[20px] w-[20px] transform rounded-full bg-white shadow-md transition-transform duration-300 ${isReportView ? "translate-x-[26px]" : "translate-x-[3px]"
                }`}
            />
          </button>
          <span className={`text-sm font-medium whitespace-nowrap ${isReportView ? "text-[#4F46E5]" : "text-gray-400"}`}>
            Report Details
          </span>
        </div>
      )}
      {/* L3 Sub-domains */}
      {subdomain &&
        subdomain.Assets &&
        subdomain.Assets.length > 0 && (
          <div className="space-y-6 p-2 ">
            <div className="flex flex-col md:flex-row justify-between items-center gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isReportView ? "Report Details" : "Assets Details"}
                </h2>
                {!isReportView && (
                  <span className="text-sm text-gray-500">
                    ( {Math.min(visibleCount, filteredRows.length)} of {filteredRows.length} entries )
                  </span>
                )}
                {isReportView && subdomain?.Reports && (
                  <span className="text-sm text-gray-500">
                    ( {subdomain.Reports.length} of  {subdomain.Reports.length} entries )
                  </span>
                )}
              </div>

              <div className="flex items-center gap-4">


                <input
                  type="text"
                  placeholder="Search ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-[250px] border border-gray-300 rounded px-3 py-1 text-sm h-[40px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {!isReportView && schemaOptions.length > 0 && (
                  <button
                    onClick={() => setFilterDrawerOpen(true)}
                    className="flex items-center gap-2 px-3 py-1 h-[40px] border border-gray-300 rounded text-sm bg-[#bb1ccc] text-white hover:bg-[#4f52d1] whitespace-nowrap"
                  >
                    <ListFilter className="h-4 w-4" />

                    Filters
                    {activeFilterCount > 0 && (
                      <span className="bg-white text-[#bb1ccc] text-xs rounded-full px-1.5 py-0.5 font-bold leading-none">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
            {
              isAddToCartEnabled && !isReportView && (

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-1">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full">
                    <button
                      className="px-2 py-1 rounded bg-[#1D7285] text-white whitespace-nowrap flex text-center items-center h-[30px]"
                      onClick={handleSelectAll}
                    >
                      Select All On Page
                    </button>
                    <button
                      className="px-2 py-1 rounded bg-[#1D7285] text-white whitespace-nowrap flex text-center items-center h-[30px]"
                      onClick={handleRemoveAll}
                    >
                      Remove All
                    </button>
                  </div>
                </div>
              )
            }
            {!isReportView && appliedChips.length > 0 && (
              <div className="px-2">
                <AppliedFilters chips={appliedChips} />
              </div>
            )}


            {/* TABLE VIEW */}
            {isReportView ? (
              <ReportDetails subdomain={subdomain} searchQuery={searchQuery} onReportClick={handleReportClick} />
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div
                  ref={tableContainerRef}
                  className="overflow-x-auto"
                  style={{ overflowY: "auto", maxHeight: "600px" }}
                >
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase w-[20%]">
                          Asset Name
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase w-[20%]">
                          Schema Name
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase w-[25%]">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase w-[20%]">
                          Quality Score
                        </th>
                        {
                          isAddToCartEnabled && (
                            <th className="px-6 py-3 text-left text-sm font-medium text-black uppercase w-[15%]">
                              Add to Cart
                            </th>
                          )
                        }

                      </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentRows.map((l3Subdomain, index) => {
                        const href = buildDataDomainUrl({
                          selectedL1: currentL1,
                          selectedL2: currentL2,
                          selectedL3: currentL3,
                          selectedL4: currentL4,
                          selectedL5: l3Subdomain.data_asset_name,
                        });

                        return (
                          <tr
                            key={l3Subdomain.data_asset_id}
                            className={`cursor-pointer ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              } hover:bg-blue-50 transition-colors`}
                            onClick={() => onL5Click(l3Subdomain.data_asset_name)}
                          >
                            <td className="px-6 flex items-center gap-3 py-4 whitespace-nowrap w-[20%]">
                              {isAddToCartEnabled && !isReportView && (
                                <input
                                  type="checkbox"
                                  className="h-6 rounded border-gray-300 text-[#bb1ccc] focus:ring-[#bb1ccc]"
                                  checked={isSelected(l3Subdomain.data_asset_id)}
                                  onChange={() => handleSelectSingle(l3Subdomain)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                              <div className="flex items-center">
                                <span className="bg-[#3da046] w-[35px] h-[35px] rounded-xl flex items-center justify-center flex-shrink-0 mr-3">
                                  <Database width="18px" color="white" />
                                </span>
                                {getPartAfterPipe(l3Subdomain.data_asset_name) &&
                                  <Tooltip
                                    title={getPartAfterPipe(
                                      l3Subdomain.data_asset_name,
                                    )}
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "black",
                                          color: "white",
                                          fontSize: "0.9rem",
                                          borderRadius: "6px",
                                        },
                                      },
                                      arrow: { sx: { color: "black" } },
                                    }}
                                  >
                                    <Link
                                      to={href}
                                      onClick={(e) => {
                                        const isModifiedClick =
                                          e.metaKey ||
                                          e.ctrlKey ||
                                          e.shiftKey ||
                                          e.altKey ||
                                          e.button !== 0;
                                        if (isModifiedClick) {
                                          return;
                                        }
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onL5Click(l3Subdomain.data_asset_name);
                                      }}
                                      className="text-sm font-medium text-gray-900 cursor-pointer"
                                    >
                                      {getPartAfterPipe(l3Subdomain.data_asset_name)}
                                    </Link>
                                  </Tooltip>}
                              </div>
                            </td>
                            <td className="px-6 py-4 w-[20%]">

                              <span className="text-sm text-gray-600">
                                {getPartBeforePipe(l3Subdomain.data_asset_name) || "N/A"}
                              </span>

                            </td>

                            <td className="px-6 py-4 w-[25%]">
                              {l3Subdomain.asset_description ? (
                                <span className="text-sm text-gray-600">
                                  {l3Subdomain.asset_description}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-600">N/A</span>
                              )}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap w-[20%]">
                              <div className="flex items-center">
                                <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                  <div
                                    className={`h-2 rounded-full transition-all duration-300 ${parseFloat(l3Subdomain.asset_score) > 95
                                      ? "bg-green-600"
                                      : parseFloat(l3Subdomain.asset_score) < 90
                                        ? "bg-red-500"
                                        : "bg-yellow-500"
                                      }`}
                                    style={{
                                      width: `${parseFloat(
                                        l3Subdomain.asset_score,
                                      )}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-medium text-gray-900">
                                  {l3Subdomain.asset_score}
                                </span>
                              </div>
                            </td>
                            {
                              isAddToCartEnabled && (
                                <td className="px-6 py-4 w-[15%]">
                                  <button
                                    className="px-3 justify-center  py-1 rounded bg-[#5558e3] text-white whitespace-nowrap flex items-center h-[37px] min-w-[128px]"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectSingle(l3Subdomain);
                                    }}
                                  >
                                    {isSelected(l3Subdomain.data_asset_id) ? (
                                      <ShoppingBag className="h-5" />
                                    ) : (
                                      <ShoppingCart className="h-5" />
                                    )}
                                    <span className="ml-[8px] text-[12px]">
                                      {isSelected(l3Subdomain.data_asset_id)
                                        ? "Added"
                                        : "Add to Cart"}
                                    </span>
                                  </button>
                                </td>
                              )
                            }

                          </tr>
                        )
                      })}
                      {/* Sentinel inside the scrollable container */}
                      <tr>
                        <td colSpan={isAddToCartEnabled ? 5 : 4} className="p-0">
                          <div ref={sentinelRef} style={{ height: 1 }} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {hasMore && (
                  <div className="flex items-center justify-center py-4 gap-2 border-t border-gray-100">
                    <img src={heartLoading} alt="Loading more..." style={{ width : 200}} />
                    <span className="text-gray-500 text-2xl">Loading more...</span>
                  </div>
                )}
                {!hasMore && filteredRows.length > 0 && (
                  <div className="text-center py-3 text-gray-400 text-xs border-t border-gray-100">
                    All {filteredRows.length} assets loaded
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      <ProductModal showProductModal={showProductModal} selectedProductForView={selectedProductForView} productLoading={productLoading} setShowProductModal={setShowProductModal} setSelectedProductForView={setSelectedProductForView} productDetails={subdomain} />
      <ReusableFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sections={filterSections}
        values={assetFilters}
        onApply={(filters) => setAssetFilters(filters)}
        onReset={() => setAssetFilters({ schema: [] })}
        flat
        title="Asset Filters"
        subtitle="Filter assets by schema"
      />
    </div>

  );
};

